import { ThemeOptions } from '@mui/material/styles';
import { radii, darkColors, brand, iconBadgeTones, buildShadows } from './tokens';
import { buildComponents } from './componentOverrides';
import { ITorqTheme } from './torqTypes';

const torq: ITorqTheme = {
  surface: {
    page: darkColors.page,
    level2: darkColors.level2,
    card: darkColors.card,
    elevated: darkColors.elevated,
  },
  border: {
    default: darkColors.borderDefault,
    strong: darkColors.borderStrong,
    focus: darkColors.borderFocus,
  },
  text: {
    primary: darkColors.textPrimary,
    secondary: darkColors.textSecondary,
    muted: darkColors.textMuted,
    disabled: darkColors.textDisabled,
  },
  hover: darkColors.hover,
  selected: darkColors.selected,
  scrollbarThumb: darkColors.scrollbarThumb,
  scrollbarThumbHover: darkColors.scrollbarThumbHover,
  dialogShadow: darkColors.dialogShadow,
  iconBadge: iconBadgeTones,
  // Paleta TORQ (spec "Menu Lateral — TORQ") — mesmo navy/ciano usado no tema
  // claro com sidebar escura, pra manter a identidade da sidebar consistente
  // independente do tema escolhido pro conteúdo.
  sidebar: {
    bg: '#001E27',
    logoAreaBg: '#011F29',
    border: '#1E2A36',
    divider: '#1E2A36',
    textPrimary: '#FFFFFF',
    textSecondary: '#CBD5E1',
    hover: 'rgba(0, 226, 244, 0.06)',
    scrollbarThumb: darkColors.scrollbarThumb,
    // Item ativo: fundo transparente — só a barra de acento lateral e a cor
    // do ícone marcam a seleção, texto continua branco (mesmo padrão do
    // tema claro com sidebar escura).
    activeGradient: 'transparent',
    activeAccentBar: '#00E2F4',
    activeText: '#FFFFFF',
    iconDefault: '#94A3B8',
    iconActive: '#00E2F4',
    submenuBg: '#012D3D',
    submenuHover: '#073C46',
    submenuSelectedBg: 'rgba(0, 226, 244, 0.18)',
    submenuTextPrimary: '#E6F7F8',
    submenuIconDefault: '#7DD3E8',
    submenuIconSelected: '#00E2F4',
  },
};

export const darkThemeOptions: ThemeOptions = {
  palette: {
    mode: 'dark',
    // contrastText #002233 (não branco): botão primário com fundo
    // `brand.primary` precisa de texto escuro em cima (guia seção 3).
    primary: { main: brand.primary, dark: brand.primaryHover, light: brand.primaryLight, contrastText: brand.menuBase },
    secondary: { main: brand.cyan, dark: '#0891B2', light: '#67E8F9', contrastText: '#052E33' },
    error: { main: brand.error, dark: brand.errorButton, light: '#FDA4AF', contrastText: '#FFFFFF' },
    warning: { main: brand.warning, dark: brand.warningButton, light: '#FCD34D', contrastText: '#1C1207' },
    success: { main: brand.success, dark: brand.successButton, light: '#6EE7B7', contrastText: '#04120C' },
    // Separado do novo ciano de `primary` pelo mesmo motivo do tema claro.
    info: { main: '#60A5FA', dark: '#3B82F6', light: '#93C5FD', contrastText: '#FFFFFF' },

    background: {
      paper: darkColors.card,
      default: darkColors.page,
    },

    text: {
      primary: darkColors.textPrimary,
      secondary: darkColors.textSecondary,
      disabled: darkColors.textDisabled,
    },

    divider: darkColors.borderDefault,
  },

  typography: {
    fontFamily: '"Plus Jakarta Sans", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',

    h1: { fontWeight: 700, color: darkColors.textPrimary, fontSize: '2.5rem', letterSpacing: '-0.02em' },
    h2: { fontWeight: 700, color: darkColors.textPrimary, fontSize: '2rem', letterSpacing: '-0.02em' },
    h3: { fontWeight: 700, color: darkColors.textPrimary, fontSize: '1.875rem', letterSpacing: '-0.015em' },
    h4: { fontWeight: 700, color: darkColors.textPrimary, fontSize: '1.5rem', letterSpacing: '-0.01em' },
    h5: { fontWeight: 650, color: darkColors.textPrimary, fontSize: '1.375rem' },
    h6: { fontWeight: 600, color: darkColors.textPrimary, fontSize: '1rem' },

    subtitle1: { color: darkColors.textSecondary },
    subtitle2: { color: darkColors.textMuted },

    body1: { color: darkColors.textPrimary, lineHeight: 1.6, fontSize: '0.875rem' },
    body2: { color: darkColors.textSecondary, lineHeight: 1.5, fontSize: '0.8125rem' },

    caption: { color: darkColors.textMuted, fontSize: '0.75rem' },
    overline: { color: darkColors.textMuted },

    button: { textTransform: 'none', fontWeight: 600, fontSize: '0.8125rem' },
  },

  shape: { borderRadius: radii.md },

  torq,

  components: buildComponents('dark', torq),

  shadows: buildShadows('dark'),
};
